import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { Props as HeaderProps } from '@/components/InvoiceReportDetailDrawer/RightContent/Preview/Header';

type TypeTitleInfo = {
  title: string;
  number: string;
  is_report_approval: boolean;
  is_editable: boolean;
  is_approvable: boolean;
};

export const TAB_KEYS = {
  ATTACHED_INVOICE: 'attached_invoice',
  TRANSACTION_DOCUMENTS: 'transaction_documents',
} as const;

export type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

export enum ActiveFileType {
  INVOICE = 'invoice',
  BUSINESS_DOCUMENT = 'business_document',
}

export type ActiveFileId = {
  type: ActiveFileType;
  id: string;
  /** For business documents, this stores the mf_file.id for PDF viewing */
  fileId?: string;
} | null;

type PaymentDetailContextType = {
  titleInfo: TypeTitleInfo;
  setTitleInfo: (titleInfo: TypeTitleInfo) => void;
  pdfViewerProps: HeaderProps | undefined;
  setPdfViewerProps: (props: HeaderProps | undefined) => void;
  isRightPaneCollapsed: boolean;
  setIsRightPaneCollapsed: (collapsed: boolean) => void;
  activeTab: TabKey;
  setActiveTab: (tab: TabKey) => void;
  activeFile: ActiveFileId;
  setActiveFile: (file: ActiveFileId) => void;
};

const Context = createContext<PaymentDetailContextType>({
  titleInfo: {
    title: '',
    number: '',
    is_report_approval: false,
    is_editable: false,
    is_approvable: false,
  },
  setTitleInfo: () => {},
  pdfViewerProps: undefined,
  setPdfViewerProps: () => {},
  isRightPaneCollapsed: false,
  setIsRightPaneCollapsed: () => {},
  activeTab: TAB_KEYS.ATTACHED_INVOICE,
  setActiveTab: () => {},
  activeFile: null,
  setActiveFile: () => {},
});

export const PaymentDetailProvider: FC<PropsWithChildren> = ({ children }) => {
  const [titleInfo, setTitleInfo] = useState<TypeTitleInfo>({
    title: '',
    number: '',
    is_report_approval: false,
    is_editable: false,
    is_approvable: false,
  });
  const [pdfViewerProps, setPdfViewerProps] = useState<HeaderProps | undefined>(
    undefined
  );
  const [isRightPaneCollapsed, setIsRightPaneCollapsed] =
    useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabKey>(TAB_KEYS.ATTACHED_INVOICE);
  const [activeFile, setActiveFile] = useState<ActiveFileId>(null);

  const value = useMemo(
    () => ({
      titleInfo,
      setTitleInfo,
      pdfViewerProps,
      setPdfViewerProps,
      isRightPaneCollapsed,
      setIsRightPaneCollapsed,
      activeTab,
      setActiveTab,
      activeFile,
      setActiveFile,
    }),
    [
      titleInfo,
      setTitleInfo,
      pdfViewerProps,
      setPdfViewerProps,
      isRightPaneCollapsed,
      setIsRightPaneCollapsed,
      activeTab,
      setActiveTab,
      activeFile,
      setActiveFile,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const usePaymentDetail = () => useContext(Context);
